import supercluster from 'points-cluster'
import pointGeometry from '@mapbox/point-geometry'
import transform from 'google-map-react/lib/utils/lib_geo/transform'
import keyBy from 'lodash.keyby'
import pickby from 'lodash.pickby'
import get from 'lodash.get'
import { List } from 'immutable'
import ReactGA from 'react-ga'
import Cookies from 'js-cookie'
// import geolib from 'geolib'

export const propertyFields = [
  'id',
  'name',
  'address',
  'description',
  'city',
  'state',
  'zip',
  'price',
  'lat',
  'lon',
  'sqft',
  'beds',
  'baths',
  'year',
  'showZestimate',
  'createdAt',
  'amenities',
  'arv',
  'repairEstimate',
  'type',
  'homeType',
  'images',
  'defaultImage',
  'startListingOn',
  'sellerLeadStage',
].join()

export const franchiseeFields = [
  'id',
  'name',
  'email',
  'phone',
  'profileImageUrl',
  'bannerImageUrl',
  'description',
  'subdomain',
  'address',
  'city',
  'state',
  'zip',
  'country',
  'lat',
  'lon',
  'location',
].join()

export const amtFmt = num => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(0).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0).replace(/\.0$/, '') + 'K'
  }
  return num
}
export const withCommas = x => {
  let val = x
  if (x) {
    val = x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return val
}

export function getClusters(properties, bounds, zoom, minZoom = 3, maxZoom = 18, radius = 90) {
  const cl = supercluster(properties, {
    minZoom, // min zoom to generate clusters on
    maxZoom, // max zoom level to cluster the points on
    radius, // cluster radius in pixels
  })
  return cl({ bounds, zoom })
}

export function getHomepageQuery(lat, lng) {
  return {
    query: `query {
        propertyCount,
        properties(geoPoint: { lat: ${lat}, lon: ${lng} }, limit: ${process.env.REACT_APP_NEW_LISTINGS_LIMIT}, order: "distance_from_me") { ${propertyFields} } }`,
  }
}

export function getBoundedQuery(geoRect, filters, queryType, id, offset = 0) {
  // Check if is a Franchisee page
  if (queryType === 'Franchisee') {
    return {
      query: `query { franchisee(id: ${id}) { properties { ${propertyFields} }}}`,
    }
  } else {
    return {
      query: `query ($geoRect: GeoRectangleInput, $filters: PropertyFilterInput) {
        propertyCount(geoRect: $geoRect, filters: $filters),
        properties(geoRect: $geoRect, filters: $filters, limit: ${100}, offset: ${offset}) { ${propertyFields} }
      }`,
      variables: { filters: sanitizeFilters(filters), geoRect },
    }
  }
}

export const getErrorMsg = err => {
  let errMsg = err

  if (Array.isArray(err)) {
    errMsg = err[0]
  } else if (err !== null && typeof err === 'object') {
    errMsg = err.errors[0].message
  }

  return errMsg || 'Oops!!! Something went wrong'
}

export const getValidationMsg = (msg, dynamicContent = {}) => {
  let message = msg

  for (let key in dynamicContent) {
    let regExp = new RegExp('{' + key + '}', 'g')
    message = message.replace(regExp, dynamicContent[key])
  }

  return message
}

const domains = process.env.REACT_APP_BASE_DOMAINS ? process.env.REACT_APP_BASE_DOMAINS.split(',') : ['locallhost', 'staging.dealvestors.com']
const baseDomains = domains
  .map(domain => [domain, 'www.' + domain])
  .reduce((a, b) => {
    return a.concat(b)
  })

export const isBaseDomain = domain => 0 <= baseDomains.indexOf(domain)

export const logger = (() => {
  const printLog = (type, msg) => {
    const key = `REACT_APP_SHOW_${type.toUpperCase()}`
    if (JSON.parse(process.env[key])) {
      console[type](...msg)
    }
  }

  const log = (...msg) => {
    printLog('log', msg)
  }

  const warn = (...msg) => {
    printLog('warn', msg)
  }

  const error = (...msg) => {
    printLog('error', msg)
  }

  return {
    log,
    warn,
    error,
  }
})()

export const getSimilarPropertiesQuery = pId => {
  return {
    query: `query { similarProperties(id: ${pId}, limit: 3) { ${propertyFields} } }`,
  }
}

export const fetchWithTimeout = (url, options, timeInMS = 7000) => {
  return new Promise((resolve, reject) => {
    fetch(url, options)
      .then(res => resolve(res.json()))
      .catch(err => reject(err))
    setTimeout(() => reject(new Error('timeout')), timeInMS)
  })
}

export const isFunction = callback => typeof callback === 'function'

/**
 * Creates bounds from a given geocoded point (location), using screen view port and a given zoom
 *
 * @param {Object} center - point location used to create the bounds
 * @param {Number} zoom
 * @returns {Object} bounds object
 */
export function getBoundsFromWindow(center, zoom) {
  const height = window.innerHeight
  const width = window.innerWidth
  const tileSize = process.env.REACT_APP_DEFAULT_TILE_SIZE
  const tr = new transform(tileSize)

  tr.scale = 512
  tr.width = width
  tr.height = height
  tr.zoom = zoom
  tr.center = center

  if (width > 0 && height > 0) {
    let topLeft = tr.pointLocation(pointGeometry.convert([-width / 2, -height / 2]))
    topLeft.lng -= 360 * Math.round(topLeft.lng / 360)
    let bottomRight = tr.pointLocation(pointGeometry.convert([width / 2, height / 2]))
    bottomRight.lng -= 360 * Math.round(bottomRight.lng / 360)

    return {
      ne: {
        lat: topLeft.lat,
        lng: bottomRight.lng,
      },
      sw: {
        lat: bottomRight.lat,
        lng: topLeft.lng,
      },
    }
  }

  throw new Error('width and height must be greater than 0')
}

/**
 * Creates a safe bounds from a given location (lat, lng) using screen viewport or if none location.loc was given, we gonna default to a given location.viewPort
 *
 * @param {Object} location - The location used to create the bounds
 * @param {Immutable.Map} location.loc - Map { geocoded latitude,geocoded longitude }
 * @param {Immutable.Map} location.viewPort - Map { northeast.lat , northeast.lng, southwest.lat, southwest.lng }
 * @returns {Object} bounds object using location.loc and screen viewport or if a location wasn't provideed will try to use a provided location.viewPort
 * @see {@link getBoundsFromWindow}
 */
export const createSafeBounds = ({ viewPort, loc }) => {
  if (loc && loc.size > 0) {
    return getBoundsFromWindow(loc.toJS(), parseInt(process.env.REACT_APP_DEFAULT_ZOOM, 10))
  } else if (viewPort && viewPort.size > 0) {
    return viewPortToBounds(viewPort)
  }

  throw new Error('shouldnt get into this state')
}

/**
 * Transform a viewPort/Inmmutable.Map into bounds/Object.
 *
 * @param {Immutable.Map} location.viewPort - Map { northeast.lat , northeast.lng, southwest.lat, southwest.lng }
 * @returns {Object}
 */
export const viewPortToBounds = viewPort => ({
  ne: {
    lat: viewPort.getIn(['northeast', 'lat']),
    lng: viewPort.getIn(['northeast', 'lng']),
  },
  sw: {
    lat: viewPort.getIn(['southwest', 'lat']),
    lng: viewPort.getIn(['southwest', 'lng']),
  },
})

export const isNewlyAddedProperty = date => {
  let diff = Math.floor((Date.parse(new Date()) - Date.parse(date)) / (24 * 60 * 60 * 1000))
  return diff <= 5
}

export const isNewlyListedProperty = date => {
  let diff = Math.floor((Date.parse(new Date()) - Date.parse(date)) / (24 * 60 * 60 * 1000))
  return diff <= 7
}

export const isOnline = () => window.navigator.onLine

export function convertPropertiesJSONToRecord(json, isFranchisee, loc) {
  const propObj = `data${isFranchisee ? '.franchisee' : ''}.properties`
  const pr = get(json, propObj, [])
  const properties = pr.map(el => [
    el.id,
    {
      ...el,
      lng: el.lon,
      // distance: loc && el.lat && el.lon && geolib.getDistanceSimple({ latitude: loc.get('lat'), longitude: loc.get('lng') }, { latitude: el.lat, longitude: el.lon }),
    },
  ])
  return properties
}

export const transformToKeyedMap = key => array => keyBy(array, key)

export const transformToIdKeyedIdMap = transformToKeyedMap('id')
export const transformToNameKeyedMap = transformToKeyedMap('name')

const TOKEN_COOKIE_NAME = 'HVA-token-' + process.env.REACT_APP_BASE_DOMAINS

export function setToken(token) {
  Cookies.set(TOKEN_COOKIE_NAME, token, { domain: `${process.env.REACT_APP_BASE_DOMAINS}` })
}

export function getToken() {
  return Cookies.get(TOKEN_COOKIE_NAME)
}

export function removeToken() {
  Cookies.remove(TOKEN_COOKIE_NAME, { domain: `${process.env.REACT_APP_BASE_DOMAINS}` })
}

export const isUserLogged = user => !!(user && getToken() && user.token === getToken() && user.id)

export const baseName = url => {
  let base = String(url).substring(url.lastIndexOf('/') + 1)
  if (-1 !== base.lastIndexOf('.')) base = base.substring(0, base.lastIndexOf('.'))
  return base
}

export const trackPage = page => {
  ReactGA.set({
    page,
  })
  ReactGA.pageview(page)
}

export const trackEvent = eventData => {
  const { category, action } = eventData
  ReactGA.event({
    category: category,
    action: action,
  })
}

export const sanitizeFilters = filters => {
  return pickby(filters, value => value && (!Array.isArray(value) || value.length) && (!List.isList(value) || value.size))
}

export const validationMessage = evaluation => {
  const warning = evaluation.feedback.warning
  const suggestions = evaluation.feedback.suggestions && evaluation.feedback.suggestions[0]
  return suggestions ? (warning ? warning + '. ' + suggestions : suggestions) : warning
}

export const filteredProperties = properties => {
  return properties.filter(prop => prop.lat && prop.lng && (!prop.type || prop.type.toLowerCase() !== process.env.REACT_APP_ASSINGMENTS_PROPERTY_TYPE))
}

export const isUserPremium = user => !!(user && getToken() && user.token === getToken() && user.isPremium)

export function firstTimeFreeUser(user) {
  return !!(user && getToken() && user.loginCount === 1 && !user.isPremium)
}

export const thumbnailUrl = url => {
  const BUCKET = process.env.REACT_APP_S3_BUCKET
  const BUCKET_URL = `https://${BUCKET}.s3.amazonaws.com`

  const thumbnailKey = url.replace(new RegExp('https?://[^/]+(/' + BUCKET + ')?/'), '').replace(/(\.[^.]+)$/, '_thumb$1')

  return `${BUCKET_URL}/${thumbnailKey}`
}



// WEBPACK FOOTER //
// ./src/utils/index.js